import { CalendarDays, Lock, Percent, ArrowRight } from 'lucide-react';
import { Button } from './ui';

export const BOOKING_OPENS_ISO = '2026-10-20';
export const FIRST_BOOK_DAYS = 14;

function cairoDateOnly(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Cairo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function formatLongDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDayMonth(iso: string) {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function bookingOpensLabel(now = new Date()) {
  return cairoDateOnly(now) < BOOKING_OPENS_ISO
    ? formatLongDate(BOOKING_OPENS_ISO)
    : formatLongDate(cairoDateOnly(now));
}

export const BOOKING_OPENS_LABEL = formatLongDate(BOOKING_OPENS_ISO);

export function EarlyPhysioOfferDialog({
  onBuy,
  onRegister,
  onDismiss,
  dismissLabel = 'Cancel',
}: {
  onBuy: () => void;
  onRegister?: () => void;
  onDismiss: () => void;
  dismissLabel?: string;
}) {
  const todayIso = cairoDateOnly();
  const beforeOpen = todayIso < BOOKING_OPENS_ISO;
  const opensLong = formatLongDate(BOOKING_OPENS_ISO);
  const opensShort = formatDayMonth(BOOKING_OPENS_ISO);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="choose-plan-title"
      aria-describedby="choose-plan-body"
      className="app-dialog bg-surface shadow-pop w-full max-w-md overflow-hidden"
    >
      <div className="relative bg-sand px-6 pt-6 pb-5 text-white overflow-hidden">
        <div
          className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-white/10"
          aria-hidden
        />
        <p className="relative text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80 mb-2 whitespace-nowrap">
          Limited seats
        </p>
        <h2 id="choose-plan-title" className="relative font-heading text-xl sm:text-2xl font-bold leading-none whitespace-nowrap">
          Limited 30% off applied
        </h2>
        <p className="relative text-xs sm:text-sm text-white/90 mt-2 whitespace-nowrap">
          {beforeOpen
            ? `Opens ${opensShort}. Paying doesn’t start plan time.`
            : `${FIRST_BOOK_DAYS} days to book your first week. Paying doesn’t start plan time.`}
        </p>
      </div>
      <div className="p-6 sm:p-7">
        <ol className="relative mb-4">
          <li className="flex gap-3 pb-4">
            <div className="flex flex-col items-center">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sand text-white">
                <Lock className="w-4 h-4" aria-hidden />
              </span>
              <span className="mt-1 w-px min-h-[1.25rem] flex-1 bg-line" aria-hidden />
            </div>
            <div className="pt-0.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-sand-strong">Today</div>
              <p className="text-sm text-ink font-medium">
                Buy now. Your plan time does not start yet.
              </p>
            </div>
          </li>
          <li className="flex gap-3 pb-4">
            <div className="flex flex-col items-center">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-soft text-teal">
                <CalendarDays className="w-4 h-4" aria-hidden />
              </span>
              <span className="mt-1 w-px min-h-[1.25rem] flex-1 bg-line" aria-hidden />
            </div>
            <div className="pt-0.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                {beforeOpen ? opensLong : `Next ${FIRST_BOOK_DAYS} days`}
              </div>
              <p className="text-sm text-ink">
                {beforeOpen
                  ? `Booking opens. You have ${FIRST_BOOK_DAYS} days to book your first clinic week.`
                  : `Book your first clinic week.`}
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sand-soft text-sand-strong font-heading text-xs font-bold">
                1st
              </span>
            </div>
            <div className="pt-0.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">First week booked</div>
              <p id="choose-plan-body" className="text-sm text-ink">
                That is when your plan time starts.
              </p>
            </div>
          </li>
        </ol>
        <div className="rounded-xl border border-sand/25 bg-sand-soft px-4 py-3 mb-4 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-sand-strong whitespace-nowrap">
            <Percent className="w-3.5 h-3.5" aria-hidden />
            Limited 30% off applied
          </span>
          <span className="text-xs font-bold text-ink whitespace-nowrap shrink-0">Limited time</span>
        </div>
        <div className="flex flex-col gap-2 mt-1">
          {onRegister && (
            <Button className="w-full" size="lg" onClick={onRegister}>
              Register for free
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Button>
          )}
          <Button className="w-full" variant={onRegister ? 'secondary' : 'primary'} size="lg" onClick={onBuy}>
            Buy plan now
            {!onRegister && <ArrowRight className="w-4 h-4" aria-hidden />}
          </Button>
          <Button className="w-full" variant="ghost" onClick={onDismiss}>
            {dismissLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
